import React, {useContext, useRef, useState} from 'react';
import {Card} from "../../../../../../components/card/card.jsx";
import {CitizenContext} from "../context/citizen-context.jsx";
import {getAssetPath} from "../../../../../../utils/get-asset-path.js";
import ModalImage from "../../../../../../components/modal/modal-image.jsx";
import {v4} from "uuid";
import {FaCircle, FaPen, FaPlus, FaUser} from "react-icons/fa";
import LifeRisk from "../../utils/life-risk.js";
import {Menu, MenuButton, MenuItem, MenuItems, Transition} from "@headlessui/react";
import ProtectionTypes from "../../utils/protection-type.js";
import initialAggressor from "../resources/initial-aggressor.js";
import Alert from "../../../../../../components/alert/alert.jsx";
import readImageFile from "../utils/read-image-file.js";
import {useRequiredTabErrors} from "../../../../../../hooks/use-required-tab-errors.js";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const CitizenTabs = ({tabSelected, setTabSelected, aggressorSelected, setAggressorSelected, isLoadingFetch}) => {
    const {citizen, setCitizen, setAttaches, tabs, aggressors, setAggressors, isDirty} = useContext(CitizenContext);
    const imgRef = useRef(undefined);
    const tabKeys = [...new Set(tabs
        .flatMap(({key, children}) => [key, ...(children?.map(({key: childKey}) => childKey) ?? [])])
        .filter(Boolean)
        .concat(citizen.protectiveMeasure ? ["aggressor"] : []))];
    const {hasRequiredTabError} = useRequiredTabErrors({
        tabKeys,
        tabSelected,
        setTabSelected,
    });

    const [imageModal, setImageModal] = useState({open: false, imgScr: null});

    const triggerInput = () => imgRef.current?.click();

    const attachUserImage = ({target: {files}}) => {
        readImageFile(files, (file, imgScr) => {
            setCitizen(prev => ({...prev, victimPicture: file.attachId, imgScr}));
            setAttaches(prev => [
                ...prev.filter(i => i.attachId !== citizen.victimPicture),
                file
            ]);
        });
    }

    const setOpenImageModal = (open) => {
        setImageModal(prev => ({...prev, open}))
    }

    const handleLifeRisk = (lifeRisk) => {
        setCitizen(prev => ({...prev, lifeRisk}));
    }

    const onClickAggressor = (id) => {
        setAggressorSelected(id);
        setTabSelected("aggressor")
    }

    const addAggressor = () => {
        const id = v4();
        setAggressors(prev => ([...prev, {id, ...initialAggressor}]));
        onClickAggressor(id);
    };

    return (
        <article id={"citizen-menu"} className={"col-span-2 flex flex-col gap-2 h-[calc(100vh-69px)]"}>
            <div className={"flex flex-col gap-1 items-center"}>
                <input type="file" className="hidden" accept="image/png, image/jpeg" onChange={attachUserImage} ref={imgRef}/>
                <button className="group h-[137px] bg-white flex relative rounded-full outline-0" onClick={triggerInput}>
                    <span className="group-hover:flex hidden absolute h-[137px] w-[137px] bg-[#333] opacity-60 rounded-full justify-center items-center">
                        <FaPen color={"#ffffff"} size={35}/>
                    </span>

                    <img
                        className={"rounded-full object-cover h-[137px] w-[137px]"}
                        src={(citizen.victimPicture && !citizen.imgScr) ? `${API_KEEPER}/ged/attach/${citizen.victimPicture}` : citizen.imgScr ? `${citizen.imgScr}` : getAssetPath("img/user.png")}
                        alt=""
                    />
                </button>

                <h3 title={citizen?.name || "Protegido"} className={"w-full truncate text-center"}>{citizen?.name || "Protegido"}</h3>
                <h5 className={"!font-normal italic w-full truncate text-center"}>{citizen?.protectionProgram?.map(p => p?.label ?? p).join(", ") || "Selecione um programa"}</h5>

                <Menu as="div" className="flex items-center justify-end">
                    <MenuButton type="button" className={"flex items-center justify-center h-full w-full gap-2 cursor-pointer"}>
                        <FaCircle className={"rounded-full"} color={LifeRisk.getLifeRiskColor(citizen?.lifeRisk)} size={"20px"}/>
                        {citizen?.lifeRisk ? "Risco " + LifeRisk.toView(citizen.lifeRisk) : "Grau de risco"}
                        <FaPen />
                    </MenuButton>
                    <Transition
                        enter="transition ease-out duration-75"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <MenuItems
                            anchor="bottom end"
                            static={true}
                            className="w-auto origin-top-right rounded-md shadow border text-[13px] bg-white [--anchor-gap:8px] focus:outline-none z-[100] flex flex-col border-[#bdbdbd] p-1 items-start"
                        >
                            {
                                LifeRisk.getLifeRisk(false, true)?.map(i => (
                                    <MenuItem key={i}>
                                        <span className={"w-full flex gap-2 p-[5px] rounded-sm hover:bg-[#d3d3d3] cursor-pointer"} onClick={() => handleLifeRisk(i.value)}>
                                            <FaCircle className={"rounded-full"} color={LifeRisk.getLifeRiskColor(i.value)} size={23}/>
                                            {i.label}
                                        </span>
                                    </MenuItem>
                                ))
                            }
                        </MenuItems>
                    </Transition>
                </Menu>
            </div>

            <Card className={"flex-1 min-h-0 flex flex-col"}>
                <Card.Body className={"flex-1 min-h-0 flex flex-col gap-2"}>
                    <section className={"flex-1 min-h-0"}>
                        <div className={"overflow-auto h-full flex gap-2 flex-col"}>
                            {
                                !tabSelected || isLoadingFetch &&
                                <>
                                    <p className="inline-block h-5 w-1/2 bg-gray-300 rounded animate-pulse"></p>
                                    <p className="inline-block h-5 w-1/2 bg-gray-300 rounded animate-pulse"></p>

                                    <section className={"pl-5 flex gap-2 flex-col"}>
                                        {
                                            Array.from({ length: 6 }, () => []).map((_,i) => (
                                                <p key={"skeleton-loader-" + i} className="inline-block pl-5 h-4 w-1/2 bg-gray-300 rounded animate-pulse"></p>
                                            ))
                                        }
                                    </section>
                                </>
                            }

                            {
                                tabSelected && !isLoadingFetch && tabs.map(({key, label, children, icon}, index) => {
                                    return (
                                        <h3
                                            key={index}
                                            className={["px-[5px] rounded-md flex gap-3 flex-col", (!key ? "select-none" : null), (key ? "hover:text-[var(--blue-dark)] !font-bold hover:underline! cursor-pointer" : ""), (tabSelected === key ? "text-[var(--blue-dark)] underline!" : null)].join(" ")}
                                            onClick={() => {
                                                if (!key) return
                                                setTabSelected(key)
                                            }}
                                        >
                                            {label}
                                            {
                                                children?.map(({key, label, icon}) => (
                                                    <p key={key} className={["box-border pl-5 flex gap-2 font-normal text-[13px] hover:text-[var(--blue-dark)] hover:!font-semibold hover:underline! rounded-md cursor-pointer items-center", (tabSelected === key ? "text-[var(--blue-dark)] !font-semibold underline!" : null), (hasRequiredTabError(key) ? "border border-[#dc2626]" : null)].filter(Boolean).join(" ")} onClick={() => setTabSelected(key)}>
                                                        {icon} {label}
                                                    </p>
                                                ))
                                            }
                                        </h3>
                                    )
                                })
                            }

                            {
                                citizen.protectiveMeasure &&
                                <>
                                    <h3 className={"px-[5px] rounded-md flex gap-3 flex-col select-none"}>
                                        Agressores
                                    </h3>
                                    {
                                        aggressors.map((a, i) => (
                                            <p
                                                key={a.id}
                                                className={["box-border pl-5 flex flex-col font-normal text-[13px] hover:text-[var(--blue-dark)] hover:!font-semibold hover:underline! rounded-md cursor-pointer", (tabSelected === "aggressor" && aggressorSelected === a.id ? "text-[var(--blue-dark)] !font-semibold underline!" : ""), (hasRequiredTabError("aggressor") ? "border border-[#dc2626]" : "")].filter(Boolean).join(" ")}
                                                onClick={() => onClickAggressor(a.id)}
                                            >
                                                <span className={"flex gap-2 items-center"}>
                                                    <FaUser /> {a.name || "Agressor " + (i+1)}
                                                </span>
                                                {a.protectionType && <span className={"pl-5 italic truncate text-[12px]"} title={ProtectionTypes.toView(a.protectionType)}>{ProtectionTypes.toView(a.protectionType)}</span>}
                                                {a.familyConnection && <span className={"pl-5 italic truncate text-[12px]"} title={a.familyConnection}>{a.familyConnection}</span>}
                                            </p>
                                        ))
                                    }

                                    <p className={"pl-5 flex gap-2 items-center font-normal text-[13px] hover:text-[var(--blue-dark)] hover:!font-semibold rounded-md cursor-pointer"} onClick={addAggressor}>
                                        <FaPlus /> Adicionar agressor
                                    </p>
                                </>
                            }
                        </div>
                    </section>

                    {
                        isDirty &&
                        <Alert target={"warning"}>
                            <span className={"text-[14px]"}>* Salve suas alterações antes de sair</span>
                        </Alert>
                    }
                </Card.Body>
            </Card>

            <ModalImage
                open={imageModal.open}
                setOpen={setOpenImageModal}
                imgUrl={imageModal.imgScr}
            />
        </article>
    );
};

export default CitizenTabs;